import React from "react";
import { Link } from "@/i18n/router";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, Globe2, HeartHandshake } from "lucide-react";
import { Seo } from "@/lib/seo";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { BRAND } from "@/data/brand";
import { PLATFORM_URL } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const PILLARS = [
  { icon: ShieldCheck, title: "Sovereign AI", text: "Artificial intelligence infrastructure aligned with European values, jurisdiction and operational independence." },
  { icon: Globe2, title: "Resilient Future", text: "Critical digital infrastructure engineered to endure — auditable, verifiable and built to last." },
  { icon: HeartHandshake, title: "Humanity First", text: "Technology in service of people, with meaningful human oversight and accountability at its core." },
];

export default function Universe() {
  const { tr } = useLang();
  return (
    <>
      <Seo
        title="AstroLife™ Universe"
        description={tr("The AstroLife™ Universe — Sovereign AI, Resilient Future, Humanity First. The brand and vision behind Europe's verifiable AI governance and sovereign digital infrastructure.")}
        path="/universe"
        keywords="AstroLife Universe, Sovereign AI, Resilient Future, Humanity First, European AI, verifiable governance"
        breadcrumbs={[{ label: tr("Universe") }]}
      />

      {/* Brand hero with the official portrait cover */}
      <section className="relative overflow-hidden border-b border-navy-border bg-navy pt-[72px]" data-testid="universe-hero">
        <div className="absolute inset-0 bg-grid opacity-20" aria-hidden="true" />
        <div className="radial-glow absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:px-8 py-16 lg:grid-cols-2 lg:py-24">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-astro-light">{tr("The AstroLife™ Universe")}</span>
            <h1 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-white">
              {tr("One vision for sovereign, trustworthy AI.")}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-silver/70">
              {tr("AstroLife™ unites verifiable AI governance, sovereign infrastructure and human-centred technology into a single, coherent universe — engineered in Europe, for the organisations that matter most.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {BRAND.tagline.map((t) => (
                <span key={t} className="rounded-full border border-astro-blue/30 bg-astro-blue/10 px-4 py-1.5 text-sm font-semibold text-astro-light">{tr(t)}</span>
              ))}
            </div>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="universe-access-platform"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-astro-blue px-8 py-4 font-semibold text-white hover:bg-astro-light transition-colors">
                {tr("Access Platform")} <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link to="/about" data-testid="universe-about-link"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/20 px-8 py-4 font-semibold text-white hover:bg-white/10 transition-colors">
                {tr("Our Story")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-full max-w-md"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-astro-blue/15 blur-3xl" aria-hidden="true" />
            <img
              src={BRAND.coverPortrait}
              alt="AstroLife™ Universe — Sovereign AI · Resilient Future · Humanity First"
              data-testid="universe-cover"
              className="relative w-full rounded-2xl border border-navy-border shadow-2xl"
              loading="eager"
            />
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Stagger className="grid gap-6 md:grid-cols-3">
            {PILLARS.map((p) => (
              <motion.div key={p.title} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy-surface p-8">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                  <p.icon className="h-6 w-6" />
                </div>
                <h2 className="mt-6 font-heading text-xl font-bold text-white">{tr(p.title)}</h2>
                <p className="mt-2 text-sm leading-relaxed text-silver/65">{tr(p.text)}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
      <PlatformCta />
    </>
  );
}
